import { useIntl } from "react-intl";
import { Skeleton } from "src/components/Skeleton/Skeleton";
import { border_radius } from "src/design-system/tokens/border";
import { default as colorDS } from "src/design-system/tokens/color";
import Trash from "src/design-system/icons/Trash.svg?react";
import Suitcase from "src/design-system/icons/Suitcase.svg?react";
import { color, spacing } from "src/theme";

import { useId, useRef, useState } from "react";
import Tooltip from "src/components/Tooltip/Tooltip";
import { A11yOutline } from "src/utils/accessibility";
import {
  desktopLayout,
  mobileLayout,
  useLayout,
} from "src/utils/hooks/useLayout";
import styled from "styled-components";
import { ClickAwayListener } from "src/components/ClickAwayListener/ClickAwayListener";
import { tw } from "src/design-system/tw";
import { Button } from "react-aria-components";
import messages from "./MyTripCardContent.messages";

type MyTripCardCallbacks = {
  onClickDelete?: () => void;
  onClick?: () => void;
};

type MyTripCardContentProps = {
  title: string;
  numDestinations: number;
  isCurrent?: boolean;
  isLoggedOut?: boolean;
};

export function MyTripCardContent(
  props: MyTripCardContentProps & MyTripCardCallbacks,
) {
  const intl = useIntl();
  const isMobile = useLayout() === "mobile";
  const id = useId();

  const [isDeleting, setIsDeleting] = useState(false);
  const confirmFocusRef = useRef<HTMLButtonElement>(null);

  function onClickRemoveButton(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    props.onClickDelete?.();
  }

  function clickHandler(event: React.MouseEvent) {
    if (!(event.target as HTMLElement).hasAttribute("data-delete")) {
      if (isDeleting) {
        setIsDeleting(false);
        return;
      }
      props.onClick?.();
    } else {
      if (isMobile) {
        setIsDeleting(true);
        confirmFocusRef.current?.focus();
      } else {
        props.onClickDelete?.();
      }
    }
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      const focusableElements = document.querySelectorAll("[data-mytripcard]");
      if (!focusableElements) {
        return;
      }
      const currentIndex = Array.from(focusableElements).findIndex(
        (element) => element === document.activeElement,
      );
      if (currentIndex === -1) {
        return;
      }
      if (event.key === "ArrowUp" && currentIndex > 0) {
        // Focus the element
        (focusableElements[currentIndex - 1] as HTMLElement).focus();
      }
      if (
        event.key === "ArrowDown" &&
        currentIndex < focusableElements.length - 1
      ) {
        (focusableElements[currentIndex + 1] as HTMLElement).focus();
      }
    }
    if (event.key === " " || event.key === "Enter") {
      props.onClick?.();
    }
  }

  return (
    <ClickAwayListener onClickAway={() => setIsDeleting(false)}>
      <MyTripCardWrapper
        title={props.title}
        onClick={clickHandler}
        isLoggedOut={props.isLoggedOut}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        data-mytripcard
        data-testid="myTripCard"
      >
        <div className="flex flex-row flex-nowrap items-start gap-3">
          <div className="rounded-md bg-gray-100 p-2.5">
            <Suitcase
              className={tw(
                "h-4 w-4 text-neutral-1600",
                props.isLoggedOut && "text-gray-400",
              )}
            />
          </div>
          <div className="max-w-full flex-1">
            <h4
              className={tw(
                "line-clamp-2 max-w-64 text-base leading-none font-medium text-ellipsis whitespace-normal text-text-secondary",
                props.isLoggedOut && "text-gray-400",
              )}
            >
              {props.title}
            </h4>
            <div className="mb-sm text-body-md leading-none text-neutral-1400">
              {intl.formatMessage(messages.destinations, {
                numDestinations: props.numDestinations,
              })}
            </div>
          </div>
          {!props.isLoggedOut && !props.isCurrent && (
            <Tooltip
              disableAnimation
              id={id}
              tooltipContent={
                <span className="block px-2 py-1 text-body-sm leading-none">
                  {intl.formatMessage(messages.deleteTrip)}
                </span>
              }
            >
              <DeleteButton
                data-delete
                onKeyDown={(e) => e.stopPropagation()}
                title={intl.formatMessage(messages.deleteTrip)}
              >
                <Trash className="h-4 w-4 text-icon-primary" />
              </DeleteButton>
            </Tooltip>
          )}
          <Button
            className={tw(
              "box-border inline-flex w-0 items-center justify-center bg-icon-error text-base leading-none font-medium text-white no-underline",
              "absolute top-0 right-0 bottom-0 m-0 cursor-pointer appearance-none overflow-hidden rounded-tr-md rounded-br-md border-none align-middle select-none",
              "transition-[width,padding-left,padding-right] duration-200 ease-in-out focus-visible:outline-3 focus-visible:-outline-offset-[2px] focus-visible:outline-border-brand",
              isDeleting && "w-18 px-4",
            )}
            ref={confirmFocusRef}
            onClick={(event) =>
              onClickRemoveButton(event as React.MouseEvent<HTMLButtonElement>)
            }
            isDisabled={!isDeleting}
            aria-hidden={!isDeleting}
          >
            {intl.formatMessage(messages.delete)}
          </Button>
        </div>
        {props.isCurrent && !props.isLoggedOut && (
          <>
            <span className="ml-4xl rounded-lg bg-transport-green-100 p-1.5 text-body-sm leading-none font-medium tracking-wider text-transport-teal-700 uppercase">
              {intl.formatMessage(messages.currentTrip)}
            </span>
          </>
        )}
      </MyTripCardWrapper>
    </ClickAwayListener>
  );
}

export function MyTripCardSkeleton() {
  return (
    <MyTripCardWrapper>
      <div className="flex flex-row flex-nowrap items-start gap-3">
        <div className="rounded-md bg-gray-50 p-2.5">
          <Suitcase className="h-4 w-4 text-gray-400" />
        </div>
        <div className="max-w-full flex-1">
          <Skeleton width="100px" height="20px" margin={`0 0 ${spacing.sm}`} />
          <Skeleton width="40px" height="10px" />
        </div>
      </div>
    </MyTripCardWrapper>
  );
}

const DeleteButton = styled.button`
  border-radius: ${border_radius.rounded_md};
  border: none;
  opacity: 0;
  width: 0;
  cursor: pointer;
  ${A11yOutline}

  > * {
    // We want all click events to happen directly on the button component.
    pointer-events: none;
  }

  &:focus-visible {
    width: unset;
    padding: ${spacing.lg};
    opacity: 1;
    svg {
      path {
        fill: ${color.red};
        stroke: ${color.red};
      }
    }
  }

  &:hover {
    background-color: ${color.white};
    svg {
      path {
        fill: ${color.red};
        stroke: ${color.red};
      }
    }
  }

  ${mobileLayout} {
    opacity: 1;
    width: unset;
    padding: ${spacing.lg};
    svg {
      path: {
        fill: ${colorDS.icon.primary};
        stroke: ${colorDS.icon.primary};
      }
    }
  }
`;

const MyTripCardWrapper = styled.li<{
  isLoggedOut?: boolean;
  isMobile?: boolean;
}>`
  list-style: none;
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: ${border_radius.rounded_md};
  ${A11yOutline};
  outline-offset: -2px;
  background-color: white;
  border: 1px solid ${colorDS.border.border};
  padding: ${spacing.lg};
  margin-bottom: ${spacing.md};

  ${desktopLayout} {
    padding: ${spacing.lg};
    border: 1px solid ${colorDS.border.border};
    margin-bottom: ${spacing.md};

    &:hover {
      background-color: ${(props) =>
        props.isLoggedOut ? "transparent" : "#f2f2f2"};

      cursor: ${(props) => (props.isLoggedOut ? "default" : "pointer")};
      ${DeleteButton} {
        width: unset;
        padding: ${spacing.md};
        opacity: 1;
      }
    }
  }
  &:focus-visible {
    ${DeleteButton} {
      width: unset;
      padding: ${spacing.lg};
      opacity: 1;
    }
  }
`;
